import { addDays, differenceInDays, format, isBefore } from 'date-fns';
import { parseDate, parseStringToJson } from '../../utilities/helpers';
import { getParamFromUrl, isEntryFlow } from '../../utilities/urlResolver';
import abTesting from '../../services/abTesting';
import { LOCALE, MARKET } from '../../constants';
import { pushEvent } from '@/utilities/analytics';

export const buildSearch = (form, prParams) => {
  const { who, when, nights, experience, style, weekendsOnly, departure, departureSelectedByUser, facilities } = form;

  let searchParams = {
    nights,
    type: 'month',
    months: [],
  };

  if (who.capacity) {
    searchParams = {
      ...searchParams,
      ...who.capacity,
    }
    if (searchParams.children > 0) {
      const existingAges = searchParams.childAges || [];
      searchParams.childAges = [
        ...existingAges,
        ...Array(searchParams.children - existingAges.length).fill(7)
      ];
    }
  }

  if (when.months.length) {
    searchParams.type = 'month';
    searchParams.months = when.months;
  }

  if (when.customHoliday) {
    const formattedStartDate = parseDate(when.customHoliday.dateFrom, 'dd-MM-yyyy');

    searchParams.type = 'range';
    searchParams.startDate = isBefore(new Date(), formattedStartDate)
      ? when.customHoliday.dateFrom
      : format(addDays(new Date(), 1), 'dd-MM-yyyy');
    searchParams.endDate = when.customHoliday.dateTo;
    searchParams.months = [];
  }

  if (when.lastMinuteDeals) {
    const lastMinuteRange = window.holidayFinder.market === 1 ? 28 : 14; // 28 days for DE, 14 days for IL
    searchParams.startDate = format(addDays(new Date(), 1), 'dd-MM-yyyy');
    searchParams.endDate = format(addDays(new Date(), lastMinuteRange + 1), 'dd-MM-yyyy');
    searchParams.type = 'range';
    searchParams.months = [];
  }

  if (prParams.startDate && prParams.endDate) {
    const today = new Date();
    const parsedEnd = parseDate(prParams.endDate, 'dd-MM-yyyy');

    // Only set the dates if endDate is in the future
    if (parsedEnd.getTime() > today.getTime()) {
      const parsedStart = parseDate(prParams.startDate, 'dd-MM-yyyy');
      if (parsedStart.getTime() <= today.getTime()) {
        console.log('Setting start date to tomorrow');
        searchParams.startDate = format(addDays(today, 1), 'dd-MM-yyyy');
        console.log(searchParams.startDate);
        searchParams.endDate = prParams.endDate;
      } else {
        searchParams.startDate = prParams.startDate;
        searchParams.endDate = prParams.endDate;
      }
      searchParams.type = 'range';
      searchParams.months = [];
    }
  }

  if (experience) {
    searchParams.what = [experience.id];
    if ([82, 88].includes(experience.id) && MARKET === 1) {
      searchParams.budget = [100, 399];
    }
    if (experience.id === 83) {
      searchParams.budget = [100, 499];
    }
  }

  if (style) {
    if (abTesting.wizardStyleAlternativeSorting && ['Basic', 'Medium'].includes(style.id)) {
      searchParams.sort = 'cheapest_per_destination';
      searchParams.order = '1';
    }
    searchParams.stars = style.stars;
  }

  if (abTesting.userPrefEnabled) {
    searchParams.sort = 'user_pref';
  }

  if (weekendsOnly) {
    searchParams.weekendsOnly = true;
  }

  if (!form.luggage && !abTesting.moveMultiRates) {
    searchParams.luggage = ['canAddTrolley'];
  }

  if (facilities.length > 0) {
    searchParams.facilities = facilities;
  } else {
    const hfUser = JSON.parse(localStorage.getItem('hf_user'));
    if (hfUser?.preferences?.general?.facilities?.facility_ids) {
      searchParams.facilities = Object.keys(hfUser.preferences.general.facilities.facility_ids);
    }
  }

  if (departure.length) {
    searchParams.fromwhere = departure.filter(dep => dep.active).map(d => d.id);
    if (departureSelectedByUser) {
      const fromwhereEndDate = new Date();
      fromwhereEndDate.setDate(fromwhereEndDate.getDate() + 365);
      setTimeout(() => {
        localStorage.setItem(
          'selectedFromwhere',
          JSON.stringify({ fromwhere: departure.filter(dep => dep.active), date: fromwhereEndDate.getTime() })
        );
      }, 5500);
    }
  } else if (localStorage.getItem('selectedFromwhere')) {
    const selectedFromWhere = JSON.parse(localStorage.getItem('selectedFromwhere')).fromwhere;
    if (selectedFromWhere.length) {
      searchParams.fromwhere = selectedFromWhere.map(d => d.id);
    }
  }

  if (prParams.destinations) {
    searchParams.destinations = prParams.destinations;
  }
  if (prParams.destinationGroups) {
    searchParams.destinationsGroups = prParams.destinationGroups;
  }
  if (prParams.themes) {
    searchParams.what = prParams.themes;
  }

  return searchParams;
}

export const getWizardV2State = () => {
  if (!window.holidayFinder.wizard_v2_payload) {
    pushEvent('wizard_v2_state is not available', {
      locale: document.documentElement.lang,
      holidayFinder: window?.holidayFinder,
      dataGlobalSettings: window?.dataGlobalSettings,
    });

    return null;
  }

  const locale = window.holidayFinder.locale || window?.dataGlobalSettings?.locale;
  const wizardV2State = parseStringToJson(window.holidayFinder.wizard_v2_payload)[locale];

  if (typeof wizardV2State === 'undefined') {
    pushEvent('wizard_v2_state locale not found', {
      locale,
      wizardV2State: parseStringToJson(window.holidayFinder.wizard_v2_payload),
    });
  }

  if (shouldHideWhatTheme()) {
    wizardV2State.stepsToShow.whatTheme = false;
    sessionStorage.setItem('hideWhatTheme', 1);
  }

  return wizardV2State;
}

const shouldHideWhatTheme = () => {
  return (getParamFromUrl('pr_what') && getParamFromUrl('pr_what') !== -1) ||
    getParamFromUrl('pr_destinations') ||
    getParamFromUrl('pr_destinationsGroups') ||
    sessionStorage.getItem('hideWhatTheme');
};

const getPreselectedWhen = (when) => {
  if (!when) return {
    months: [],
    customHoliday: null,
    lastMinuteDeals: false,
  };

  const { whenStep } = getWizardV2State();
  const { months } = when;
  let selectedMonths = [];
  let customHolidaySelected = null;
  let lastMinuteDealsSelected = false;

  if (months?.periods.length > 0) {
    selectedMonths = months.periods.map((period) => format(parseDate(period.start, 'dd/MM/yyyy'), 'M', new Date()));
  }

  if (when.flexible && when.flexible.start && when.flexible.end) {
    const selectedHoliday = whenStep.holidays.find(
      (holiday) =>
        holiday.dateFrom.replace(/-/g, '/') === when.flexible.start &&
        holiday.dateTo.replace(/-/g, '/') === when.flexible.end
    );

    if (selectedHoliday) {
      selectedMonths = [];
      lastMinuteDealsSelected = false;
      customHolidaySelected = selectedHoliday;
    } else {
      selectedMonths = [format(parseDate(when.flexible.start, 'dd/MM/yyyy'), 'M', new Date())]
    }
  }

  if (when.specific?.start) {
    const selectedHoliday = whenStep.holidays.find(
      (holiday) =>
        holiday.dateFrom.replace(/-/g, '/') === when.specific.start &&
        holiday.dateTo.replace(/-/g, '/') === when.specific.end
    );

    if (selectedHoliday) {
      selectedMonths = [];
      lastMinuteDealsSelected = false;
      customHolidaySelected = selectedHoliday;
    } else {
      selectedMonths = [format(parseDate(when.specific.start, 'dd/MM/yyyy'), 'M', new Date())]
    }
  }

  return {
    months: selectedMonths,
    customHoliday: customHolidaySelected,
    lastMinuteDeals: lastMinuteDealsSelected,
  };
};

const getPreselectedNights = (when) => {
  if (!when) return [];

  const { months, flexible, specific } = when;

  if (months?.nights) {
    return months.nights;
  }

  if (flexible?.nights) {
    return flexible.nights;
  }

  if (specific?.start && specific?.end) {
    return [differenceInDays(parseDate(specific.end, 'dd/MM/yyyy'), parseDate(specific.start, 'dd/MM/yyyy'))];
  }

  return [];
};

const getPreselectedStyle = (filters) => {
  const { hostingLevelPrefStep } = getWizardV2State();
  if (getParamFromUrl('pr_hostingLevelPref')) {
    const selectedStyle = hostingLevelPrefStep.items.find(style =>
      style.id.toLowerCase() === getParamFromUrl('pr_hostingLevelPref').toLowerCase()
    );

    if (selectedStyle) {
      return selectedStyle;
    }
  } else if (filters.rating) {
    const selectedStyle = hostingLevelPrefStep.items.find(style =>
      style.stars.length === filters.rating.length &&
      style.stars.every(star => filters.rating.includes(star))
    );

    if (selectedStyle) {
      return selectedStyle;
    }

    return null;
  }

  return null;
}

export const getInitialV2State = () => {
  let hfLastFilters = JSON.parse(localStorage.getItem('hfLastFilters'));
  if (typeof hfLastFilters === 'string') {
    localStorage.removeItem('hfLastFilters');
    hfLastFilters = null;
  }
  const wizardData = getWizardV2State();

  const defaultState = {
    who: {},
    when: {
      months: [],
      customHoliday: null,
      lastMinuteDeals: false,
    },
    nights: [],
    experience: null,
    style: null,
    weekendsOnly: false,
    departure: [],
    departureSelectedByUser: true,
    prompt: '',
    promptType: 'text', // 'text' | 'audio',
    whisper: null,
    facilities: [],
  }

  if (hfLastFilters && wizardData) {
    const { engine, filters } = hfLastFilters;
    let initialWho = wizardData.whoStep.items.find(item => item.capacity.adults === engine.who.adult && item.capacity.children === engine?.who?.child);

    if (initialWho && initialWho.capacity && engine.who.adult && initialWho.capacity.adults === engine.who.adult && initialWho.capacity.children === engine.who.child) {
      initialWho.capacity.childAges = engine.who.childAges;
    }

    // If selected capacity does not match default selection, make custom selection
    if (!initialWho && engine.who.adult) {
      initialWho = {
        capacity: {
          adults: engine.who.adult,
          children: engine.who.child,
          childAges: engine.who.childAges,
        }
      }
    }

    if (getParamFromUrl('pr_capacity')) {
      initialWho = wizardData.whoStep.items.find(item => item.id === getParamFromUrl('pr_capacity'));
      if (getParamFromUrl('pr_capacity') === 'family' || getParamFromUrl('pr_capacity') === 'friends') {
        initialWho.capacity = {};
      }
    }

    const sdDefaultOpt = window.holidayFinder?.similarDestinationsDefaultOpt || +localStorage.getItem('similarDestinationsDefaultOpt') || 2;

    const presetData = {
      who: initialWho || {},
      when: getPreselectedWhen(engine.when),
      nights: getPreselectedNights(engine.when),
      experience: (engine.what?.length > 0 && engine.what[0]) || null,
      style: getPreselectedStyle(filters),
      weekendsOnly: engine.when?.months?.weekends_only || engine.when?.flexible?.weekends_only || false,
      similarDestinations: (
        wizardData.similarDestinationsStep &&
        sdDefaultOpt &&
        wizardData.similarDestinationsStep.items[sdDefaultOpt - 1].value
      ),
      departure: [],
      departureSelectedByUser: true,
      prompt: '',
      promptType: 'text',
      whisper: null,
      facilities: [],
    }

    if (isEntryFlow() || isEntryFlow('link-presets-welcome')) {
      return presetData
    }

    const who = initialWho?.capacity?.children > 0 ? initialWho : {}
    return {
      ...defaultState,
      who,
    }
  }

  return defaultState;
}

export const getShowCapacityTotalButton = (form) => {
  if (getParamFromUrl('pr_capacity')) { return false; }
  if (form.who?.capacity?.children > 0) {
    return form.who?.capacity
    && form.who.capacity?.children === form.who.capacity.childAges?.length;
  }

  return false;
}

export const transformWizardDataToFilters = (form) => {
  const data = {
    locale: LOCALE,
    currency: window.holidayFinder.currency,
    fromwhere: [],
    engine: {
      market: window.holidayFinder.market,
      where: Array.isArray(form.destinations) ? form.destinations : form.destinations ? [form.destinations] : [],
      when: {},
      who: {
        adult: form.adults,
        child: form.children,
        room: 1,
        childAges: form.childAges
      },
      what: form.what,
      whereTxt: [],
      whatTxt: [],
      destinationGroups: Array.isArray(form.destinationGroups) ? form.destinationGroups : [form.destinationGroups] || []
    },
    filters: {
      rating: form.stars,
      stops: [],
      refundable: false,
      board: [],
      amenities: [],
      amenitiesTxt: [],
      luggage: {
        canAddTrolley: false,
        canAddCib: false
      },
      flex: false
    },
    sort: {
      best: -1
    },
    limit: 20,
    offset: 0,
    searchProfileVersion: 1
  }

  if (form.months) {
    data.engine.when = {
      months: form.months,
      nights: form.nights,
    }
  }

  if (form.type === 'range') {
    data.engine.when = {
      flexible: {
        start: form.startDate,
        end: form.endDate,
        nights: form.nights,
      }
    }
  }

  return data;
}
